/**
 * Shared campaign utility functions.
 * Used across PublicDashboard, CampaignCard, CampaignDetail, etc.
 */

// ─── Category Labels (Korean) ───
export const CATEGORY_LABELS: Record<string, string> = {
  accommodation: '숙박',
  restaurant: '음식점',
  cafe: '카페',
  tour: '투어',
  ticket: '티켓',
  shopping: '쇼핑',
  wellness: '웰니스',
  other: '기타',
};

// ─── Channel Labels ───
export const CHANNEL_LABELS: Record<string, string> = {
  instagram: '인스타그램',
  blog: '블로그',
  youtube: '유튜브',
};

// ─── Gradient Palettes for cards ───
export const GRADIENT_PALETTES = [
  'from-rose-500 to-orange-400',
  'from-violet-600 to-indigo-400',
  'from-emerald-500 to-teal-400',
  'from-sky-500 to-cyan-400',
  'from-fuchsia-500 to-pink-400',
  'from-amber-500 to-yellow-400',
  'from-blue-600 to-violet-500',
  'from-teal-500 to-emerald-400',
] as const;

// ─── Date Helpers ───

/** Format ISO date string to YYYY-MM-DD */
export const formatDate = (dateStr?: string | null): string =>
  dateStr ? dateStr.slice(0, 10) : '';

/** Calculate days remaining from now to endDate. Returns null if no endDate. */
export const getDaysRemaining = (endDate?: string | null): number | null => {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/** Calculate progress percentage between start and end dates */
export const getProgressPercent = (start?: string | null, end?: string | null): number => {
  if (!start || !end) return 0;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const now = Date.now();
  if (now >= e) return 100;
  if (now <= s) return 0;
  return Math.round(((now - s) / (e - s)) * 100);
};

// ─── Campaign Helpers ───

/** Get content channels from campaign (supports both array and legacy single field) */
export const getChannels = (campaign: any): string[] =>
  (campaign.contentChannels?.length
    ? campaign.contentChannels
    : campaign.contentChannel
      ? [campaign.contentChannel]
      : []
  ).filter(Boolean);

/** Get gradient for a given index */
export const getGradient = (index: number): string =>
  GRADIENT_PALETTES[index % GRADIENT_PALETTES.length];

/** Get gradient based on brand name hash */
export const gradientForBrand = (name?: string): string => {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return GRADIENT_PALETTES[Math.abs(hash) % GRADIENT_PALETTES.length];
};

/** Get category label in Korean */
export const getCategoryLabel = (category?: string | null): string =>
  category ? (CATEGORY_LABELS[category] || category) : '';

/** Get channel label in Korean */
export const getChannelLabel = (channel: string): string =>
  CHANNEL_LABELS[channel] || channel;

/** Format recruitment period display */
export const formatRecruitmentPeriod = (period?: any): string => {
  if (!period) return '';
  if (period.alwaysRecruiting) return '상시모집';
  const start = formatDate(period.startDate);
  const end = formatDate(period.endDate);
  return end ? `${start} ~ ${end}` : start;
};

/** Check if recruitment is still open */
export const isRecruitmentOpen = (period?: any): boolean => {
  if (!period) return false;
  if (period.alwaysRecruiting) return true;
  if (!period.endDate) return true;
  return new Date(period.endDate).getTime() > Date.now();
};
